import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import { authApi } from "@/api/auth.api";
import { tokenStore } from "@/auth/token";
import { useAuthStore } from "@/auth/useAuthStore";
import { authKeys } from "@/queries/auth.queries";
import { processApiError } from "@/utils/errors";
import { APP_URL, UAE_PASS_LOGOUT_URL } from "@/const/env";
import { APP_ROUTES } from "@/const/routes";
import type { ILoginPayload, IVerifyEmailPayload } from "@/types/auth";
import { SignOutReason } from "@/types/auth";
import { analytics } from "@/lib/analytics";

export const useLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: ILoginPayload) => authApi.login(payload),
    onSuccess: async (data) => {
      tokenStore.set(data.accessToken);
      const meInfo = await queryClient.fetchQuery({
        queryKey: authKeys.me(),
        queryFn: authApi.getMe,
      });
      useAuthStore.getState().setMeInfo(meInfo);
      analytics.identify(meInfo.id, { email: meInfo.email, $first_name: meInfo.firstName, $last_name: meInfo.lastName });
      analytics.track('Login', { method: 'email' });
      const savedPath = sessionStorage.getItem("redirectPath");
      sessionStorage.removeItem("redirectPath");
      await navigate({ to: savedPath ?? APP_ROUTES.app.index });
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isLoggedInViaUAEPass, uaePassIdToken, clearAuth } = useAuthStore();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: async () => {
      tokenStore.clear();
      clearAuth();
      queryClient.clear();
      Sentry.setUser(null);
      analytics.track('Logout');
      analytics.reset();

      if (isLoggedInViaUAEPass) {
        try {
          const { logoutUrl } = await authApi.uaePassLogoutUrl({
            idTokenHint: uaePassIdToken ?? undefined,
            postLogoutRedirectUri: APP_URL,
          });
          window.location.href = logoutUrl;
        } catch {
          window.location.href = UAE_PASS_LOGOUT_URL;
        }
      } else {
        navigate({
          to: APP_ROUTES.login,
          search: { reason: SignOutReason.USER_ACTION },
        });
      }
    },
  });
};

export const useUaePassLogin = () => {
  const setUaePassError = useAuthStore((s) => s.setUaePassError);

  return useMutation({
    meta: { suppressGlobalError: true },
    mutationFn: () => authApi.uaePassLogin(),
    onSuccess: (data) => {
      tokenStore.setUaePassState(data.state);
      window.location.href = data.authUrl;
    },
    onError: (error) => {
      setUaePassError(processApiError(error, false));
    },
  });
};

export const useUaePassCallback = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setLoggedInViaUAEPass = useAuthStore((s) => s.setLoggedInViaUAEPass);
  const setUaePassIdToken = useAuthStore((s) => s.setUaePassIdToken);
  const setUaePassError = useAuthStore((s) => s.setUaePassError);

  return useMutation({
    mutationFn: async ({ code, state }: { code: string; state: string }) => {
      const savedState = tokenStore.getUaePassState();
      if (!savedState || savedState !== state) {
        throw new Error("Invalid UAE Pass state parameter");
      }
      tokenStore.clearUaePassState();
      return authApi.uaePassCallback(code, state);
    },
    onSuccess: async (data) => {
      tokenStore.set(data.accessToken);
      setLoggedInViaUAEPass(true);
      if (data.uaePass?.idToken) {
        setUaePassIdToken(data.uaePass.idToken);
      }
      const meInfo = await queryClient.fetchQuery({
        queryKey: authKeys.me(),
        queryFn: authApi.getMe,
      });
      useAuthStore.getState().setMeInfo(meInfo);
      analytics.identify(meInfo.id, { email: meInfo.email, $first_name: meInfo.firstName, $last_name: meInfo.lastName });
      analytics.track('Login', { method: 'uae_pass' });
      await navigate({ to: APP_ROUTES.app.index });
    },
    meta: { suppressGlobalError: true },
    onError: (error) => {
      const message = processApiError(error, false);
      setUaePassError(message);
      navigate({ to: APP_ROUTES.login });
    },
  });
};

export const useSendEmailVerificationLink = () => {
  return useMutation({
    mutationFn: () => authApi.sendEmailVerificationLink(),
    onSuccess: () => {
      toast.success("Verification email sent");
    },
  });
};

export const useVerifyEmail = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: IVerifyEmailPayload) => authApi.verifyEmail(payload),
    onSuccess: () => {
      navigate({ to: APP_ROUTES.app.index });
    },
  });
};
